import { apiSlice } from "../../app/api/apiSlice"
import { logOut, setCredentials, setAuthLoading, setAuthError, updateUserProfile } from "./authSlice"

export const authApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        login: builder.mutation({
            query: credentials => ({
                url: '/api/v1/user/signin',
                method: 'POST',
                body: { ...credentials }
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    console.log('Login successful data:', data);
                    dispatch(setCredentials({ token: data.token, user: data.user }));
                    dispatch(setAuthLoading('succeeded')); 
                } catch (err) {
                    console.error('Login error:', err);
                    dispatch(setAuthError(err.data?.message || 'Login failed.'));
                    dispatch(setAuthLoading('failed'));
                }
            }
        }),
        sendLogout: builder.mutation({
            query: () => ({
                url: '/api/v1/user/logout',
                method: 'POST',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    console.log('Logout successful data:', data)
                    dispatch(logOut())
                    setTimeout(() => {
                        dispatch(apiSlice.util.resetApiState())
                    }, 1000)
                } catch (err) {
                    console.error('Logout error:', err)
                    dispatch(setAuthError(err.data?.message || 'Logout failed.'));
                }
            }
        }),
        refresh: builder.mutation({
            query: () => ({
                url: '/api/v1/user/refresh',
                method: 'GET',
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    console.log('Refresh successful data:', data)
                    dispatch(setCredentials({ token: data.token, user: data.user }))
                } catch (err) {
                    console.error('Refresh error:', err)
                    dispatch(logOut()); 
                    dispatch(setAuthError(err.data?.message || 'Session expired. Please log in again.'));
                }
            }
        }),
        toggleSponsoredStatus: builder.mutation({
            query: ({ userId, isSponsored }) => ({
                url: `/api/v1/admin/user/${userId}/toggle-sponsored`,
                method: 'PATCH',
                body: { isSponsored },
            }),
            async onQueryStarted({ userId, isSponsored }, { dispatch, queryFulfilled, getState }) {
                const state = getState();
                const currentUser = state.auth.user;

                let patchResult;
                if (currentUser && currentUser._id === userId) {
                    patchResult = dispatch(apiSlice.util.updateQueryData('getUsers', 'usersList', (draft) => {
                        const userIndex = draft.findIndex(user => user._id === userId);
                        if (userIndex !== -1) {
                            draft[userIndex].isSponsoredMember = isSponsored;
                            if (isSponsored) {
                                draft[userIndex].membershipStatus = 'SPONSORED';
                                draft[userIndex].membershipTier = 'PREMIUM';
                            } else {
                                if (draft[userIndex].membershipStatus === 'SPONSORED') {
                                    draft[userIndex].membershipStatus = 'INACTIVE';
                                    draft[userIndex].membershipTier = 'NONE';
                                }
                            }
                        }
                    }));
                } else {
                    patchResult = dispatch(apiSlice.util.updateQueryData('getUsers', 'usersList', (draft) => {
                        const userIndex = draft.findIndex(user => user._id === userId);
                        if (userIndex !== -1) {
                            draft[userIndex].isSponsoredMember = isSponsored;
                            if (isSponsored) {
                                draft[userIndex].membershipStatus = 'SPONSORED';
                                draft[userIndex].membershipTier = 'PREMIUM';
                            } else {
                                if (draft[userIndex].membershipStatus === 'SPONSORED') {
                                    draft[userIndex].membershipStatus = 'INACTIVE';
                                    draft[userIndex].membershipTier = 'NONE';
                                }
                            }
                        }
                    }));
                }

                try {
                    const { data } = await queryFulfilled;
                    console.log('Toggle sponsored status successful:', data);
                    dispatch(updateUserProfile(data.user));
                } catch (err) {
                    console.error('Toggle sponsored status failed:', err);
                    patchResult.undo();
                    dispatch(setAuthError(err.data?.message || 'Failed to update sponsored status.'));
                }
            }
        }),
        signUp: builder.mutation({
            query: credentials => ({
                url: '/api/v1/user/signup',
                method: 'POST',
                body: { ...credentials }
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    console.log('SignUp successful data:', data);
                    dispatch(setAuthLoading('succeeded'));
                    return { data: { success: data.success, message: data.message, verifyUserToken: data.token, user: data.user } };

                } catch (err) {
                    console.error('SignUp error:', err);
                    dispatch(setAuthError(err.data?.message || 'Registration failed.'));
                    dispatch(setAuthLoading('failed'));
                    throw err; // Keep this throw as the component uses .unwrap()
                }
            }
        }),
        verifySignup: builder.mutation({
            query: ({ token, verifyUserToken }) => ({
                url: '/api/v1/user/verifyotp',
                method: 'POST',
                body: { token, verifyUserToken }
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    console.log('Verify OTP successful data:', data);
                    dispatch(setCredentials({ token: data.token, user: data.user }));
                    dispatch(setAuthLoading('succeeded'));
                } catch (err) {
                    console.error('Verify OTP error:', err);
                    dispatch(setAuthError(err.data?.message || 'OTP verification failed.'));
                    dispatch(setAuthLoading('failed'));
                    // Removed `throw err;` from here to allow unwrap() in the component to handle the promise rejection.
                }
            }
        }),
        // ✅ नया म्यूटेशन: OTP को फिर से भेजने के लिए
        resendOtp: builder.mutation({
            query: ({ email }) => ({
                url: '/api/v1/user/resend-otp', // ✅ यह आपके बैकएंड के नए राउट से मेल खाएगा
                method: 'POST',
                body: { email } // या mobile, जो भी आप उपयोग करते हैं
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    console.log('Resend OTP successful data:', data);
                    dispatch(setAuthLoading('succeeded'));
                    // हम यहां क्रेडेंशियल सेट नहीं करते क्योंकि यह सिर्फ OTP फिर से भेज रहा है, लॉग इन नहीं कर रहा है।
                } catch (err) {
                    console.error('Resend OTP error:', err);
                    dispatch(setAuthError(err.data?.message || 'Failed to resend OTP.'));
                    dispatch(setAuthLoading('failed'));
                    throw err; // `verifyotp.js` में `unwrap()` द्वारा पकड़ा जाएगा
                }
            }
        }),
    })
})

export const {
    useLoginMutation,
    useSendLogoutMutation,
    useRefreshMutation,
    useToggleSponsoredStatusMutation,
    useSignUpMutation,
    useVerifySignupMutation,
    useResendOtpMutation, // ✅ useResendOtpMutation को एक्सपोर्ट करें
} = authApiSlice
